import React, { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, Grid3x3, List, Package, Tag, ShoppingCart, Filter } from "lucide-react";
import { categories } from "../mock";
import { productsByCategory, subcategoriesOf, serviceCategories } from "../data/products";
import { PageHeader } from "./Contatti";
import { useToast } from "../hooks/use-toast";
import ServiceQuoteBlock from "../components/ServiceQuoteBlock";

const formatPrice = (v) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(v);

const Prodotti = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [activeSub, setActiveSub] = useState("Tutte");
  const [view, setView] = useState("grid");

  const activeCat = slug ? categories.find((c) => c.slug === slug) : null;
  const serviceData = slug ? serviceCategories[slug] : null;
  const products = useMemo(() => (slug ? productsByCategory[slug] || [] : []), [slug]);
  const subs = useMemo(() => (slug ? ["Tutte", ...subcategoriesOf(slug)] : []), [slug]);

  const filtered = useMemo(() => {
    let r = products;
    if (activeSub !== "Tutte") r = r.filter((p) => p.subcategory === activeSub);
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.code.toLowerCase().includes(q) ||
          (p.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return r;
  }, [products, activeSub, query]);

  const addToQuote = (p) => {
    toast({ title: "Aggiunto al preventivo", description: `${p.code} \u2014 ${p.name}` });
  };

  return (
    <>
      <PageHeader
        title={activeCat ? activeCat.name : "Catalogo Prodotti"}
        subtitle={
          activeCat
            ? `Listino dedicato ai rivenditori e professionisti con P.IVA. Prezzi IVA esclusa.`
            : "Scopri tutte le nostre categorie di prodotti professionali."
        }
      />

      <section className="py-12 bg-gray-50 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="bg-white rounded-2xl border border-gray-100 p-6 h-fit lg:sticky lg:top-32">
            <div className="flex items-center gap-2 border border-gray-200 rounded-full px-4 py-2 mb-6 focus-within:border-[#00A859] transition-colors">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca prodotto o codice"
                className="flex-1 outline-none text-sm bg-transparent"
              />
            </div>

            <h3 className="font-bold text-[#0B2545] uppercase tracking-wide text-xs mb-3">Categorie</h3>
            <ul className="space-y-1 mb-6">
              <li>
                <Link
                  to="/prodotti"
                  className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                    !slug ? "bg-[#00A859]/10 text-[#00A859] font-bold" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Tutte le categorie
                </Link>
              </li>
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/prodotti/${c.slug}`}
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                      slug === c.slug ? "bg-[#00A859]/10 text-[#00A859] font-bold" : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>

            {activeCat && subs.length > 1 && (
              <>
                <h3 className="font-bold text-[#0B2545] uppercase tracking-wide text-xs mb-3 flex items-center gap-1.5">
                  <Filter className="w-3 h-3" /> Sottocategorie
                </h3>
                <ul className="space-y-1">
                  {subs.map((s) => (
                    <li key={s}>
                      <button
                        onClick={() => setActiveSub(s)}
                        className={`w-full text-left block px-3 py-2 rounded-lg text-sm transition-colors ${
                          activeSub === s
                            ? "bg-[#0B2545] text-white font-bold"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {s}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="mt-8 bg-[#00A859]/5 border border-[#00A859]/20 rounded-xl p-4">
              <div className="text-xs font-bold text-[#00A859] uppercase tracking-wider mb-1">Listino B2B</div>
              <p className="text-xs text-gray-600 leading-relaxed">
                Prezzi riservati a rivenditori e professionisti con P.IVA. Sconti su quantit\u00e0.
              </p>
            </div>
          </aside>

          {/* Main */}
          <div>
            {!serviceData && (
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="text-sm text-gray-600">
                  {activeCat ? (
                    <>
                      <span className="font-bold text-[#0B2545]">{filtered.length}</span> prodotti
                      {activeSub !== "Tutte" && (
                        <> in <span className="font-semibold">{activeSub}</span></>
                      )}
                    </>
                  ) : (
                    `${categories.reduce((a, c) => a + c.sub.length, 0)} prodotti disponibili`
                  )}
                </div>
                <div className="flex items-center gap-1.5 bg-white rounded-full p-1 border border-gray-200">
                  <button
                    onClick={() => setView("grid")}
                    className={`p-2 rounded-full transition-colors ${
                      view === "grid" ? "bg-[#00A859] text-white" : "text-gray-500 hover:text-[#0B2545]"
                    }`}
                    aria-label="Vista griglia"
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setView("list")}
                    className={`p-2 rounded-full transition-colors ${
                      view === "list" ? "bg-[#00A859] text-white" : "text-gray-500 hover:text-[#0B2545]"
                    }`}
                    aria-label="Vista lista"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {!activeCat && (
              <div className="grid sm:grid-cols-2 gap-5">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/prodotti/${c.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 transition-all"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-[#0B2545] mb-2 group-hover:text-[#00A859] transition-colors">{c.name}</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {c.sub.slice(0, 3).map((s) => (
                          <span key={s} className="text-xs text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">{s}</span>
                        ))}
                        <span className="text-xs text-[#00A859] font-semibold px-2.5 py-1">+{c.sub.length - 3} altre</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {activeCat && serviceData && (
              <ServiceQuoteBlock data={serviceData} />
            )}

            {activeCat && !serviceData && filtered.length > 0 && view === "grid" && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((p) => <ProductCard key={p.code} p={p} onAdd={addToQuote} />)}
              </div>
            )}

            {activeCat && !serviceData && filtered.length > 0 && view === "list" && (
              <div className="space-y-3">
                {filtered.map((p) => <ProductRow key={p.code} p={p} onAdd={addToQuote} />)}
              </div>
            )}

            {activeCat && !serviceData && products.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#0B2545] mb-2">Listino in aggiornamento</h3>
                <p className="text-gray-600 mb-5 max-w-md mx-auto">
                  Stiamo aggiornando il listino di questa categoria. Contattaci per richiedere un preventivo personalizzato.
                </p>
                <Link to="/contatti" className="inline-flex items-center gap-2 bg-[#00A859] hover:bg-[#00874A] text-white font-bold px-6 py-3 rounded-full text-sm uppercase tracking-wide transition-colors">
                  Richiedi un preventivo
                </Link>
              </div>
            )}

            {activeCat && !serviceData && products.length > 0 && filtered.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#0B2545] mb-2">Nessun prodotto trovato</h3>
                <p className="text-gray-600">Prova a modificare i filtri o la ricerca.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

const ProductCard = ({ p, onAdd }) => (
  <div className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-[#00A859] transition-all flex flex-col">
    <div className="aspect-square overflow-hidden bg-gray-50 relative">
      <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-[#0B2545] font-bold text-[10px] px-2.5 py-1 rounded-full uppercase tracking-wider border border-gray-100">
        {p.code}
      </span>
      {p.stock === "Disponibile" && (
        <span className="absolute top-3 right-3 bg-[#00A859] text-white font-bold text-[10px] px-2.5 py-1 rounded-full uppercase tracking-wider">
          Disponibile
        </span>
      )}
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-2">
        <Tag className="w-3 h-3 text-[#00A859]" /> {p.subcategory}
      </div>
      <h3 className="font-bold text-[#0B2545] text-sm leading-snug mb-2 line-clamp-2 min-h-[40px]">{p.name}</h3>
      {p.tags && (
        <div className="flex flex-wrap gap-1 mb-3">
          {p.tags.slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">{t}</span>
          ))}
        </div>
      )}
      <div className="mt-auto pt-3 border-t border-gray-100">
        <div className="mb-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">{p.pack}</div>
          <div className="text-xl font-extrabold text-[#0B2545] leading-none mt-1">
            {formatPrice(p.price)}
            <span className="text-xs font-medium text-gray-500 ml-1">/{p.unit}</span>
          </div>
          {p.packPrice && p.packPrice !== p.price && (
            <div className="text-xs text-[#00A859] font-semibold mt-0.5">
              {formatPrice(p.packPrice)} / confezione
            </div>
          )}
        </div>
        <button onClick={() => onAdd(p)} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A859] hover:bg-[#00874A] text-white font-bold text-xs uppercase tracking-wide px-4 py-2.5 rounded-full transition-colors">
          <ShoppingCart className="w-3.5 h-3.5" /> Aggiungi al preventivo
        </button>
      </div>
    </div>
  </div>
);

const ProductRow = ({ p, onAdd }) => (
  <div className="bg-white rounded-2xl border border-gray-100 hover:border-[#00A859] hover:shadow-md transition-all p-4 flex flex-col sm:flex-row gap-5 items-start sm:items-center">
    <div className="w-full sm:w-32 aspect-square shrink-0 rounded-xl overflow-hidden bg-gray-50">
      <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">
        <span className="bg-gray-100 px-2 py-0.5 rounded-full text-[#0B2545]">{p.code}</span>
        <span className="text-[#00A859]">{p.subcategory}</span>
      </div>
      <h3 className="font-bold text-[#0B2545] text-base mb-1.5">{p.name}</h3>
      <div className="flex flex-wrap gap-1.5">
        {(p.tags || []).map((t) => (
          <span key={t} className="text-[10px] text-gray-600 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full">{t}</span>
        ))}
      </div>
      <div className="text-xs text-gray-500 mt-1.5">{p.pack}</div>
    </div>
    <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto">
      <div className="text-2xl font-extrabold text-[#0B2545] leading-none">
        {formatPrice(p.price)}
        <span className="text-xs font-medium text-gray-500 ml-1">/{p.unit}</span>
      </div>
      {p.packPrice && p.packPrice !== p.price && (
        <div className="text-xs text-[#00A859] font-semibold">{formatPrice(p.packPrice)} / conf.</div>
      )}
      <button onClick={() => onAdd(p)} className="inline-flex items-center justify-center gap-2 bg-[#00A859] hover:bg-[#00874A] text-white font-bold text-xs uppercase tracking-wide px-5 py-2.5 rounded-full transition-colors">
        <ShoppingCart className="w-3.5 h-3.5" /> Preventivo
      </button>
    </div>
  </div>
);

export default Prodotti;
